/*
 * Inline text editing tool for Raphaël 2.0 & compatible with Raphaël Free transform.
 * Source: https://github.com/marmelab/Raphael.InlineTextEditing
 * Licensed under the MIT license
 */
 import Raphael from 'raphael';

(function (root, factory) {
	if (typeof define === "function" && define.amd) {
		// AMD. Register as an anonymous module.
		define(["raphael"], function(Raphael) {
			// Use global variables if the locals are undefined.
			return factory(Raphael || root.Raphael);
		});
	} else {
		// RequireJS isn't being used. Assume Raphael is loaded in <script> tag
		factory(Raphael);
	}
}(this, function(Raphael) {

	Raphael.fn.inlineTextEditing = function(subject, options, callback) {

		// Store instance of the Raphael paper
		var paper = this;

		subject.inlineTextEditing = {
			paper : paper,
			input: null,

			/**
			 * Start text editing by hiding the current element and adding a text field at the same position
			 * @return jQuery input element
			 */
			startEditing: function( editOptions ){
				// Store Raphael container above the svg
				var container      = this.paper.canvas.parentNode;
				var translateX	    = 0;
				var translateY	    = 0;
				var transformOrder  = {};

				// Retrieve element transformation
				var rotation        = subject._.deg;
				var scaleX          = subject._.sx;
				var scaleY          = subject._.sy;
				var matrix          = subject.node.getAttribute('transform');

				// Check if the element has translations & retrieve transformations order
				for(var i = 0, length = subject._.transform.length; i < length; i++){
					var matrixComponents = subject._.transform[i];
					var transform = matrixComponents[0].toLowerCase();
					transformOrder[transform] = transform;

					if(transform == 't'){
						translateX += matrixComponents[1];
						translateY += matrixComponents[2];
					}
				}


				// Check if there is implicit matrix
				for(var i = 0, length = subject._.transform.length; i < length; i++){
					if(subject._.transform[i][0].toLowerCase() == 'm'){
						var matrixComponents = subject._.transform[i].slice(1);

						// Perform transformation from matrix elements
						rotation  += -1 * Math.asin(matrixComponents[2]) * 180 / Math.PI;
						scaleX    *= matrixComponents[0] / Math.cos(rotation*Math.PI/180);
						scaleY    *= matrixComponents[3] / Math.cos(rotation*Math.PI/180);

						transformOrder = {r: 'r', s:'s'};
					}
				}

				// Remove transformation on the current element to retrieve original dimension
				subject.node.removeAttribute('transform');

                let isTextArea = options && options.type === "textarea";
				var originalBbox  = subject._getBBox();
				//var width         = originalBbox.width;
				var width         = 300;
				var height        = originalBbox.height;
                if( editOptions?.bbox ) {
                    width = Math.max( editOptions.bbox.width + 30, 100 );
                    height = Math.max( editOptions.bbox.height, 20 );
                }
				// var x             = container.offsetLeft + subject.attrs.x + translateX;
				var x             = (subject.attrs.x - width/2) + translateX; //+ container.offsetLeft;
				var y             = container.offsetTop + subject.attrs.y - height / 2 + translateY;
				var sTransform    = '';
				var sOrigin       = 'center center';
				var oTransform    = {
					//	t : 'translate('+(translateX)+'px, '+(translateY)+'px)',
					r : 'rotate('+rotation+'deg)',
					s : 'scale('+scaleX+', '+scaleY+')'
				};

				// Build transform CSS property in the same order as the element
				for(var transform in transformOrder){
					if(oTransform[transform] != undefined){
						sTransform += oTransform[transform] + ' ';
					}
				}

				// Re-apply stored transformation to the element and hide it
				if (matrix) {
					subject.node.setAttribute("transform", matrix);
				}

				subject.hide();

				// Prepare input styles
				var oStyles = {
					position: 'absolute',
					background: 'none',
					left: x+'px',
					top: y+'px',
					width: width+'px',

					// 'text-align': 'justify',
					'-moz-text-align-last': 'center', /* Firefox 12+ */
					'text-align-last': 'center',

					'-moz-transform-origin': sOrigin,
					'-ms-transform-origin': sOrigin,
					'-o-transform-origin': sOrigin,
					'-webkit-transform-origin': sOrigin,
					'transform-origin': sOrigin,

					'-moz-transform' : sTransform,
					'-ms-transform' : sTransform,
					'-o-transform' : sTransform,
					'-webkit-transform' : sTransform,
					'transform' : sTransform
				};
                if( isTextArea ) {
                    oStyles.height = height+'px';
                    oStyles['z-index'] = '2000';
                    oStyles['background-color'] = 'white'; // see also editOptions.bgColor below
                }
                if( editOptions?.color ) {
                    oStyles.color = editOptions.color;
                }
                if( editOptions?.bgColor ) {
                    oStyles['background-color'] = editOptions.bgColor;
                }

				// Retrieve font styles
				var aFontAttributes = ['font', 'font-family', 'font-size', 'font-style', 'font-weight', 'font-variant'/*, 'line-height'*/];

				for(var i = 0, length = aFontAttributes.length; i < length; i++){
					var attribute = aFontAttributes[i];

					if(subject.attrs[attribute] != undefined){
						oStyles[attribute] = subject.attrs[attribute];
					}

					if(subject.node.style[attribute] != undefined){
						oStyles[attribute] = subject.node.style[attribute];
					}
				}

				var sStyles = '';
				for(var z in oStyles){
					sStyles += z + ':' + oStyles[z] + ';';
				}

                // Dynamically set input type
                var _element;
				switch(options.type) {
                    case 'text':
                        _element = 'input';
                        break;
                    default:
                        _element = 'textarea';
                        break;
                }
                // Create an input element with these styles
				this.input = document.createElement(_element);
				this.input.value = subject.attrs.text ? subject.attrs.text.replace(/\'/g,"\\\'") : '';
				this.input.setAttribute("style", sStyles);

				// Add aria-label for accessibility if provided in options
				if (options && options.ariaLabel) {
					this.input.setAttribute("aria-label", options.ariaLabel);
				}

				this.input.addEventListener('keyup', this._handleKeyDown.bind(this));
                if( isTextArea ) {
                    // see https://stackoverflow.com/questions/5570390/resize-event-for-textarea
                    this.inputResizeObserver = new ResizeObserver(()=>this._repositionButtonOnResize()).observe(this.input);
                }

				// Add the input in the container and apply focus on it
				container.appendChild(this.input);

                // add an OK Button
                const textInputRect = { x:x, y:y, width:width, height:height },
                    containerRect = { x:0, y:0, width:subject.paper.width, height:subject.paper.height };
                this.button = this._makeOkButton( textInputRect, containerRect, editOptions );
                container.appendChild(this.button);

				this.input.focus();
				return this.input;
			},

			/**
			 * Apply text modification and remove associated input
			 */
			stopEditing: function(){

				// Set the new the value
				// subject.attr("text", this.input.value);
				var text = this.input.value,
                    noVisibleCharacters = /^\s*$/; // matches '', '  ', etc.
				if ( noVisibleCharacters.test(text) /*text == ""*/ ){
			        text = "_";  // #1335 if no visible characters text bbox fails, so use "_" instead
				}
				subject.attr("text", text);

				// Show the text element
				subject.show();

				// Remove text input
				this.input.parentNode.removeChild(this.input);
                this.button.parentNode.removeChild(this.button);
			},

			_handleKeyDown: function(e){
				var tmp               = document.createElement("span");
				var text              = this.input.value;
				tmp.setAttribute('style', this.input.style.cssText);
				tmp.style.height      = null;
				tmp.style.width       = null;
				tmp.style.visibility  = 'hidden';
				tmp.innerHTML         = text.split('\n').join('<br />');

				this.input.parentNode.appendChild(tmp);

				// On every key down, it shrinks the width, ignoring it for now.
				//this.input.style.width = tmp.offsetWidth + "px";
				//this.input.style.height = tmp.offsetHeight + "px";

				tmp.parentNode.removeChild(tmp);
			},

            /** move the button when the text area moves (for resizable textareas only) */
            _repositionButtonOnResize: function() {
                function getRect(el) {
                    return { x:el.offsetLeft, y:el.offsetTop, width:el.offsetWidth, height: el.offsetHeight };
                }

                if( this.input?.parentElement) {
                    let textInputRect = getRect( this.input ),
                        containerRect = getRect( this.input.parentElement );
                    let sStyles = this._positionButton( textInputRect, containerRect, {} );
                    this.button.setAttribute( "style", sStyles );
                }
            },

            /** return the styles needed to position the button next to the text input element */
            _positionButton( textInputRect, containerRect, editOptions ) {
                const size = 21,
                    margin = 10,
                    textRight = textInputRect.x + textInputRect.width,
                    textCenterX = textInputRect.x + textInputRect.width/2,
                    textBottom = textInputRect.y + textInputRect.height,
                    textCenterY = textInputRect.y + textInputRect.height/2,
                    canFitRight = (textRight+margin+size ) <= (containerRect.x + containerRect.width);
                // place the icon on the right if enough space, else place on bottom (even if not enough space)
                let iconX = (canFitRight) ? textRight+margin : textCenterX - size/2,
                    iconY = (canFitRight) ? textCenterY - size/2 : textBottom + margin;
                let oStyles = {
                    position: 'absolute',
                    left: iconX + 'px',
                    top: iconY + 'px',
                    width: size + 'px',
                    height: size + 'px',
                    padding: '2px',
                    'font-size': '12px',
                    'border-radius': '14%',
                    'z-index': '2000', // float on top of all plot elements (especially title/margin opacity rects)
                }
                let sStyles = '';
                for(var z in oStyles){
                    sStyles += z + ':' + oStyles[z] + ';';
                }
                return sStyles;
            },

            /** make a checkbox icon for the user to press to end editing.
             * But actually pressing tab or clicking outside the edit box ends editing, so all we need is
             * an icon with no click handlers. */
            _makeOkButton: function( textInputRect, containerRect, editOptions ) {
                // create icon
                let button = document.createElement('button'),
                    i = document.createElement('i');
                i.classList.add('tde-i');
                i.classList.add('tde-i-tick'); // match icon in tde.font.js
                button.appendChild(i);
                button.classList.add('tde-btn-plain-filled');

                // style list for absolute positioning
                let sStyles = this._positionButton( textInputRect, containerRect, editOptions );
                button.setAttribute("style", sStyles);

                return button;
            }
		};

		return subject.inlineTextEditing;
	}

}));
